<!--
  A view that lays out the about form
-->
<template>
  <div>

    <form class="about_form">
      <!-- Subtitle -->
      <h2 class="subtitle">{{ $t(`${pagei18nKey}.title`) }}</h2>

      <!-- First name -->
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.firstName`) }}</label>
        <div class="control has-icons-right">
          <input
            v-model="firstName"
            v-validate="'required'"
            class="input"
            :class="{'is-danger': errors.has('firstName') }"
            :data-vv-as="$t(`${pagei18nKey}.firstName`)"
            name="firstName"
            type="text"
            :placeholder="$t(`${pagei18nKey}.firstName`)"
            :autocomplete="isKiosk ? 'nope' : 'given-name'">
          <span
            v-show="fields.firstName && fields.firstName.valid"
            class="icon is-small is-right">
            <i class="fas fa-check"></i>
          </span>
        </div>
        <p
          v-show="errors.has('firstName')"
          class="help is-danger">
          {{ errors.first('firstName') }}
        </p>
      </div>

      <!-- Surname -->
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.surname`) }}</label>
        <div class="control has-icons-right">
          <input
            v-model="surname"
            v-validate="'required'"
            :data-vv-as="$t(`${pagei18nKey}.surname`)"
            class="input"
            :class="{'is-danger': errors.has('surname') }"
            name="surname"
            type="text"
            :placeholder="$t(`${pagei18nKey}.surname`)"
            :autocomplete="isKiosk ? 'nope' : 'family-name'">
          <span
            v-show="fields.surname && fields.surname.valid"
            class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
        </div>
        <p
          v-show="errors.has('surname')"
          class="help is-danger">
          {{ errors.first('surname') }}
        </p>
      </div>

      <!-- Email -->
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.emailAddress`) }}</label>
        <div class="control has-icons-right">
          <input
            v-model="email"
            v-validate="'required|email'"
            :data-vv-as="$t(`${pagei18nKey}.emailAddress`)"
            class="input"
            :class="{'is-danger': errors.has('email') }"
            name="email"
            type="email"
            inputmode="email"
            :placeholder="$t(`${pagei18nKey}.emailAddress`)"
            autocapitalize="none"
            :autocomplete="isKiosk ? 'nope' : 'email'">
          <span
            v-show="fields.email && fields.email.valid"
            class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
        </div>
        <p
          v-show="errors.has('email')"
          class="help is-danger">
          {{ errors.first('email') }}
        </p>
      </div>

      <!-- Phone number -->
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.phoneNumber`) }}</label>
        <div class="control has-icons-right">
          <input
            v-model="phone"
            v-validate="{ required: true, regex: /^[0-9 ]{8,20}$/ }"
            :data-vv-as="$t(`${pagei18nKey}.phoneNumber`)"
            class="input"
            :class="{'is-danger': errors.has('phone') }"
            name="phone"
            type="tel"
            inputmode="tel"
            :placeholder="$t(`${pagei18nKey}.phoneNumber`)"
            :autocomplete="isKiosk ? 'nope' : 'tel'">
          <span
            v-show="fields.phone && fields.phone.valid"
            class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
        </div>
        <p
          v-show="errors.has('phone')"
          class="help is-danger">
          {{ errors.first('phone') }}
        </p>
      </div>

      <!-- Phone number 2 -->
      <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.phoneNumber2`) }}</label>
        <div class="control has-icons-right">
          <input
            v-model="phone2"
            v-validate="{ required: false, regex: /^[0-9 ]{8,20}$/ }"
            :data-vv-as="$t(`${pagei18nKey}.phoneNumber2`)"
            class="input"
            :class="{'is-danger': errors.has('phone2') }"
            name="phone2"
            type="tel"
            inputmode="tel"
            :placeholder="$t(`${pagei18nKey}.phoneNumber2`)">
          <span
            v-show="fields.phone2 && fields.phone2.valid"
            class="icon is-small is-right">
          <i class="fas fa-check"></i>
        </span>
        </div>
        <p
          v-show="errors.has('phone2')"
          class="help is-danger">
          {{ errors.first('phone2') }}
        </p>
      </div>
    </form>


    <!-- Button -->
    <!--
        We need this button wrapper to set the background color
        and styling behind the button
    -->
    <div class="about_button__container">
      <ms-navigation-bar
        :buttons="[ backButton, nextButton ]"
        @navigationButtonClick="navigationButtonClick"
      />
    </div>

    <!-- Form validation pop up -->
    <ms-app-message
      v-if="showErrorModal"
      :title="$t(`message.formErrorTitle`)"
      :isOpen.sync="showErrorModal">
      <div>{{ $t(`message.formErrorMessage`) }}</div>
    </ms-app-message>

  </div>
</template>
<script>
  import AppSelect from '@/components/app-select'
  import {ButtonStates} from '@/components/app-button'
  import MsNavigationBar, {DefaultNavigationButtons} from '@/components/ms-navigation-bar'
  import MsAppMessage from '@/components/ms-app-message'
  import appSettings from '@/app-settings.json'

  export default {
    name: 'AboutView',

    components: {
      AppSelect,
      MsNavigationBar,
      MsAppMessage
    },

    data: function () {
      return {
        pagei18nKey: 'message.about',
        showErrorModal: false,
        isKiosk: !!appSettings.kioskSettings
      }
    },
    methods: {
      /**
       * On navigation button clicked change view based on clicked button id
       */
      navigationButtonClick: function (buttonId) {
        if (buttonId === DefaultNavigationButtons.BACK.id) {
          this.$router.go(-1)
        } else if (buttonId === DefaultNavigationButtons.NEXT.id) {
          // checks if form is valid before changing route
          this.$validator.validate().then(valid => {
            if (this.isFormValid) {
              this.$router.push('/address')
            } else {
              this.showErrorModal = true
            }
          })
        }
      },

      /**
       * Get the index of an object that has a matching key
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - The data to check in
       * @param {string} key - The key to check value in each object
       */
      getIndexOfObjectFromKey: function (value, data, key) {
        let foundIndex = data.findIndex(item => {
          return item[key] === value
        })
        return foundIndex === -1 ? null : foundIndex
      }
    },

    computed: {
      firstName: {
        /**
         * Gets current stored firstName
         */
        get () {
          return this.$store.getters.firstName
        },

        /**
         * Sets firstName value
         *
         * @param {string} value - Value to set firstName to
         */
        set (value) {
          this.$store.commit('setFirstName', value)
        }
      },

      surname: {
        /**
         * Gets current stored surname
         */
        get () {
          return this.$store.getters.surname
        },

        /**
         * Sets surname value
         *
         * @param {string} value - Value to set surname to
         */
        set (value) {
          this.$store.commit('setSurname', value)
        }
      },

      email: {
        /**
         * Gets current stored email address
         */
        get () {
          return this.$store.getters.email
        },

        /**
         * Sets email address value
         *
         * @param {string} value - Value to set email address to
         */
        set (value) {
          this.$store.commit('setEmail', value)
        }
      },

      phone: {
        /**
         * Gets current stored phone number
         */
        get () {
          return this.$store.getters.phone
        },

        /**
         * Sets phone number value
         *
         * @param {string} value - Value to set phone number to
         */
        set (value) {
          this.$store.commit('setPhone', value)
        }
      },

      phone2: {
        /**
         * Gets current stored phone number
         */
        get () {
          return this.$store.getters.phone2
        },

        /**
         * Sets phone number value
         *
         * @param {string} value - Value to set phone number to
         */
        set (value) {
          this.$store.commit('setPhone2', value)
        }
      },

      /**
       * Get back button object
       */
      backButton: {
        get () {
          return DefaultNavigationButtons.BACK
        }
      },

      /**
       * Get next button object
       */
      nextButton: {
        get () {
          // Clone DefaultNavigationButtons.NEXT and remove reference to it
          let defaultNextButton = JSON.parse(JSON.stringify(DefaultNavigationButtons.NEXT))
          return Object.assign(
            {
              // state: this.isFormValid ? ButtonStates.ENABLED : ButtonStates.DISABLED
              state: ButtonStates.ENABLED
            },
            defaultNextButton
          )
        }
      },

      /**
       * Checks each form field to see if the form is valid
       *
       * @returns {boolean} Whether form is valid
       */
      isFormValid () {
        let valid = Object.keys(this.fields).every(key => this.fields[key].valid)
        return valid
      }
    }
  }
</script>

<style lang="scss">
  @import "../../assets/sass/main.scss";
  @import "./about-view.scss";
</style>
